import { useEffect, useState } from "react";
import api from "@/lib/api";
import { SectionTitle } from "@/components/common";
import { History, Search } from "lucide-react";

const ACTION = {
  create: { label: "Creazione", cls: "text-emerald-500 bg-emerald-400/10 border-emerald-400/30" },
  update: { label: "Modifica", cls: "text-[#38BDF8] bg-[#38BDF8]/10 border-[#38BDF8]/30" },
  elimina_definitivo: { label: "Eliminato definitivamente", cls: "text-red-600 bg-red-50 border-red-300" },
  svuota_cestino: { label: "Cestino svuotato", cls: "text-red-600 bg-red-50 border-red-300" },
  update_checklist: { label: "Aggiorn. documenti", cls: "text-[#FFB020] bg-[#FFB020]/10 border-[#FFB020]/30" },
  convert_to_immobile: { label: "Conversione incarico", cls: "text-[#FC82ED] bg-[#FC82ED]/10 border-[#FC82ED]/30" },
  pubblica: { label: "Pubblicazione", cls: "text-emerald-500 bg-emerald-400/10 border-emerald-400/30" },
  add_comunicazione: { label: "Comunicazione", cls: "text-slate-600 bg-slate-100 border-slate-300" },
  reassign: { label: "Riassegnazione", cls: "text-[#FC82ED] bg-[#FC82ED]/10 border-[#FC82ED]/30" },
  feedback: { label: "Feedback visita", cls: "text-[#38BDF8] bg-[#38BDF8]/10 border-[#38BDF8]/30" },
  richiesta_reset_password: { label: "Richiesta reset password", cls: "text-[#FFB020] bg-[#FFB020]/10 border-[#FFB020]/30" },
  password_cambiata: { label: "Password cambiata", cls: "text-[#FC82ED] bg-[#FC82ED]/10 border-[#FC82ED]/30" },
  reset_password: { label: "Password temporanea", cls: "text-[#FC82ED] bg-[#FC82ED]/10 border-[#FC82ED]/30" },
  blocca_account: { label: "Account bloccato", cls: "text-red-600 bg-red-50 border-red-300" },
  sblocca_account: { label: "Account sbloccato", cls: "text-emerald-500 bg-emerald-400/10 border-emerald-400/30" },
};
const ENTITY = { notizia: "Notizia", immobile: "Immobile", proprietario: "Proprietario", acquirente: "Acquirente", visita: "Visita", comunicazione: "Comunicazione", utente: "Utente" };

function EmailLog() {
  const [apri, setApri] = useState(false);
  const [rows, setRows] = useState(null);
  useEffect(() => { if (apri && !rows) api.get("/email-log").then((r) => setRows(r.data)).catch(() => setRows([])); }, [apri, rows]);
  const COL = { inviata: "text-emerald-600", fallita: "text-red-600", bloccata: "text-[#9A6A00]", "non inviata": "text-red-600" };
  return (
    <div className="card-immo p-4 mb-5" data-testid="email-log">
      <button data-testid="email-log-apri" onClick={() => setApri(!apri)} className="font-display font-bold text-sm">{apri ? "▾" : "▸"} Registro email inviate</button>
      {apri && <div className="mt-3 max-h-96 overflow-y-auto divide-y divide-[var(--border)] text-xs">
        {rows === null ? <div className="skeleton h-10 rounded" /> : rows.length === 0 ? <div className="text-[var(--muted)]">Nessuna email registrata.</div> :
          rows.map((r, i) => <div key={i} className="grid grid-cols-[130px_1fr_80px] sm:grid-cols-[150px_220px_1fr_90px] gap-2 py-1.5" data-testid={`email-log-riga-${i}`}>
            <span className="mono text-slate-600">{fmtDT(r.at)}</span><span className="truncate font-medium">{r.to}</span><span className="truncate hidden sm:block">{r.subject}</span>
            <span className={`font-semibold ${COL[r.stato] || ""}`} title={r.errore || ""}>{r.stato}</span></div>)}
      </div>}
    </div>
  );
}

const fmtDT = (iso) => {
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" }); }
  catch { return iso; }
};

function Values({ data }) {
  if (!data || typeof data !== "object") return <span className="text-[var(--muted)]">—</span>;
  const entries = Object.entries(data).filter(([k]) => !["updated_at", "created_at", "created_by"].includes(k));
  if (!entries.length) return <span className="text-[var(--muted)]">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {entries.slice(0, 6).map(([k, v]) => (
        <span key={k} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 border border-[var(--border)] text-[11px]">
          <span className="text-[var(--muted)]">{k}:</span>
          <span className="font-medium text-slate-700 truncate max-w-[160px]">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
        </span>
      ))}
    </div>
  );
}

export default function Storico() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [entity, setEntity] = useState("all");
  const [action, setAction] = useState("all");

  useEffect(() => {
    api.get("/audit-log").then((r) => { setItems(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const filtered = items.filter((a) =>
    (entity === "all" || a.entity === entity) &&
    (action === "all" || a.action === action) &&
    (!q || (a.user_name || "").toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <div className="fade-up">
      <SectionTitle sub="Registro cronologico di tutte le modifiche ai record. Riservato alla Direzione.">Storico Modifiche</SectionTitle>
      <EmailLog />

      <div className="flex items-center gap-2 mb-5 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
          <input data-testid="storico-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per utente…"
                 className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-white border border-[var(--border)] focus:border-[#FC82ED] outline-none text-sm" />
        </div>
        <select data-testid="storico-entity" value={entity} onChange={(e) => setEntity(e.target.value)}
                className="px-3 py-2.5 rounded-lg bg-white border border-[var(--border)] focus:border-[#FC82ED] outline-none text-sm">
          <option value="all">Tutte le entità</option>
          {Object.entries(ENTITY).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select data-testid="storico-action" value={action} onChange={(e) => setAction(e.target.value)}
                className="px-3 py-2.5 rounded-lg bg-white border border-[var(--border)] focus:border-[#FC82ED] outline-none text-sm">
          <option value="all">Tutte le azioni</option>
          {Object.entries(ACTION).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {loading ? <div className="space-y-2">{[...Array(6)].map((_, i) => <div key={i} className="skeleton h-14 rounded-xl" />)}</div> :
      filtered.length === 0 ? (
        <div className="card-immo p-12 text-center"><History className="mx-auto text-[var(--muted)] mb-3" size={32} /><p className="text-[var(--muted)] text-sm">Nessuna modifica registrata.</p></div>
      ) : (
        <div className="card-immo overflow-hidden p-0">
          <div className="hidden lg:grid grid-cols-[150px_160px_150px_1fr] gap-4 px-4 py-3 border-b border-[var(--border)] bg-[#F7F7F8] text-[10px] font-bold uppercase tracking-wider text-[var(--muted)]">
            <div>Data e ora</div><div>Utente</div><div>Azione</div><div>Dettagli</div>
          </div>
          <div className="divide-y divide-[var(--border)]">
            {filtered.map((a) => {
              const act = ACTION[a.action] || { label: a.action, cls: "text-slate-600 bg-slate-100 border-slate-300" };
              return (
                <div key={a.id} data-testid={`audit-${a.id}`} className="grid lg:grid-cols-[150px_160px_150px_1fr] gap-2 lg:gap-4 px-4 py-3 items-start hover:bg-slate-50/60 transition-colors">
                  <div className="text-xs text-slate-600 mono">{fmtDT(a.timestamp)}</div>
                  <div className="text-sm font-medium text-[#111111]">{a.user_name || "—"}</div>
                  <div className="flex flex-col gap-1">
                    <span className={`inline-flex w-fit items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide border ${act.cls}`}>{act.label}</span>
                    <span className="text-[10px] text-[var(--muted)] uppercase">{ENTITY[a.entity] || a.entity}</span>
                  </div>
                  <div className="text-xs">
                    {a.old_value && (
                      <div className="mb-1"><span className="text-[10px] font-bold uppercase text-[var(--muted)] mr-1">Prima</span><Values data={a.old_value} /></div>
                    )}
                    <div><span className="text-[10px] font-bold uppercase text-[#FC82ED] mr-1">{a.old_value ? "Dopo" : "Valore"}</span><Values data={a.new_value} /></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
